/**
 * AI CONTEXT
 * Purpose: displays current project name + progress (inline or full variant).
 * Inputs: current session + project docs.
 * Used by: TeacherAttendance and AttendanceViewSessionSummary.
 * Importance: lightweight project progress readout in operational views.
 * Refactor: consolidate inline/full display into a single variant component API.
 */
import { Progress } from "antd"
import { AnimatePresence } from "motion/react"
import { useMemo } from "react"

import AnimateInFlex, { ANIMATION_BY_KEY } from "#blocks/AnimateInFlex"
import Flex from "#blocks/Flex"
import TextOverrun from "#blocks/TextOverrun"
import { ClassSessionConstructor } from "#customTypes/composedTypes/classSessionTypes"
import { ProjectConstructor } from "#customTypes/composedTypes/projectTypes"

type ProjectInfo = {
  name: string
  fraction: number
  daysToComplete: number
  id: string
}

const DEFAULT_PROJECT_INFO: ProjectInfo = {
  name: "Unknown",
  fraction: 0,
  daysToComplete: 0,
  id: "",
}

const WeekProgress = ({
  fraction,
  daysToComplete,
}: {
  fraction: number
  daysToComplete: number
}) => {
  const percent =
    daysToComplete === 0 ? 0 : Math.round((fraction / daysToComplete) * 100)
  return (
    <AnimateInFlex
      animation={ANIMATION_BY_KEY.leftSm}
      key={`${fraction}-${daysToComplete}`}
    >
      <Flex col style={{ fontSize: 12, width: "fit-content" }} gap={-10}>
        Week {fraction} / {daysToComplete}
        <Progress
          showInfo={false}
          style={{ width: "100%" }}
          percent={percent}
        />
      </Flex>
    </AnimateInFlex>
  )
}

const InlineProjectInfo = ({ projectInfo }: { projectInfo: ProjectInfo }) => {
  const { id, fraction, daysToComplete, name } = projectInfo

  const weekText = ` - Wk ${fraction} / ${daysToComplete}`

  return (
    <AnimateInFlex
      animation={ANIMATION_BY_KEY.leftSm}
      key={`${id}-${name}-${fraction}-${daysToComplete}`}
      style={{
        fontSize: 10,
        marginTop: -3,
        fontWeight: 600,
        flexWrap: "nowrap",
      }}
    >
      <div
        style={{ width: "fit-content", marginRight: 5, whiteSpace: "nowrap" }}
      >
        Current Project:
      </div>
      <Flex
        style={{
          maxWidth: 165,
          flexWrap: "nowrap",
        }}
      >
        <TextOverrun text={name} />
      </Flex>
      <div style={{ width: "fit-content", marginLeft: 5 }}>{weekText}</div>
    </AnimateInFlex>
  )
}

const ProjectInfo = ({ projectInfo }: { projectInfo: ProjectInfo }) => {
  const { id, fraction, daysToComplete, name } = projectInfo

  return (
    <AnimateInFlex
      col
      animation={ANIMATION_BY_KEY.leftSm}
      gap={12}
      style={{
        padding: "24px 40px",
      }}
    >
      <h1 style={{ fontSize: 14 }}>Current Project: </h1>
      <h2 style={{ fontSize: 28, display: "flex", gap: 32 }}>
        <AnimateInFlex
          animation={ANIMATION_BY_KEY.leftLg}
          animationKey={`${id}-${name}`}
        >
          {name}
        </AnimateInFlex>
        <WeekProgress
          key={`${id}-${fraction}-${daysToComplete}`}
          fraction={fraction}
          daysToComplete={daysToComplete}
        />
      </h2>
    </AnimateInFlex>
  )
}

const SessionProjectInfo = ({
  session,
  project,
  inline,
}: {
  session: ClassSessionConstructor | undefined
  project: ProjectConstructor | undefined
  inline?: boolean
}) => {
  const projectInfo = useMemo(() => {
    if (!project) return DEFAULT_PROJECT_INFO

    const result = { ...DEFAULT_PROJECT_INFO }
    result.name = project.name
    result.fraction = session?.projectFraction ?? 0
    result.daysToComplete = parseInt(project.daysToComplete)
    result.id = project.id
    return result
  }, [project, session])

  const isLoading = !project || !session

  return (
    <AnimatePresence mode="sync">
      {isLoading ? null : inline ? (
        <InlineProjectInfo projectInfo={projectInfo} />
      ) : (
        <ProjectInfo projectInfo={projectInfo} />
      )}
    </AnimatePresence>
  )
}
export default SessionProjectInfo
